import { useState, useEffect } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { DiscordIcon } from '@/features/auth/components/BrandIcons';
import { GoogleIcon } from '@/features/auth/components/BrandIcons';
import { settingsTranslations } from './translations';

import { API_ORIGIN } from '@/lib/api-origin';

interface LinkedData {
  discord: { id: string; linkedAt: string } | null;
  google: { id: string; email: string; linkedAt: string } | null;
}

export function LinkedAccounts() {
  const t = useSectionTranslation(settingsTranslations);
  const [data, setData] = useState<LinkedData | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const fetch = async () => {
    const res = await api.get<LinkedData>('/auth/linked-accounts');
    if (!isApiError(res)) setData(res.data);
  };

  useEffect(() => { fetch(); }, []);

  const unlink = async (provider: string) => {
    setUnlinking(provider);
    await api.del(`/auth/linked-accounts/${provider}`);
    setUnlinking(null);
    fetch();
  };

  const providers = [
    {
      key: 'discord' as const,
      title: t.linkedAccounts.discord.title,
      desc: t.linkedAccounts.discord.desc,
      Icon: DiscordIcon,
      iconColor: 'text-[#5865F2]',
      href: `${API_ORIGIN}/api/auth/discord`,
    },
    {
      key: 'google' as const,
      title: t.linkedAccounts.google.title,
      desc: t.linkedAccounts.google.desc,
      Icon: GoogleIcon,
      iconColor: '',
      href: `${API_ORIGIN}/api/auth/google`,
    },
  ];

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-lg font-semibold text-brand-white mb-1">{t.linkedAccounts.title}</h2>
        <p className="text-sm text-brand-muted">{t.linkedAccounts.desc}</p>
      </div>

      {providers.map((p) => {
        const linked = data?.[p.key];
        const unlinkLabel = t.linkedAccounts[p.key].unlink;
        return (
          <div key={p.key} className="rounded-xl bg-surface-card border border-surface-border p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-hover">
                  <p.Icon size={20} className={p.iconColor || undefined} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-white">{p.title}</p>
                  {linked ? (
                    <p className="text-xs text-brand-muted">
                      {t.linkedAccounts.linkedAs}{' '}
                      <span className="text-brand-white">{p.key === 'google' ? (linked as { email: string }).email : (linked as { id: string }).id}</span>
                      {' · '}{t.linkedAccounts.linkedSince} {new Date(linked.linkedAt).toLocaleDateString()}
                    </p>
                  ) : (
                    <p className="text-xs text-brand-muted">{t.linkedAccounts.notLinked}</p>
                  )}
                </div>
              </div>

              {linked ? (
                <button
                  type="button"
                  onClick={() => unlink(p.key)}
                  disabled={unlinking === p.key}
                  className="text-xs font-medium text-brand-danger hover:underline disabled:opacity-50"
                >
                  {unlinking === p.key ? (
                    <span className="flex items-center gap-1"><Loader2 size={11} className="animate-spin" />{t.linkedAccounts[p.key].linking}</span>
                  ) : unlinkLabel}
                </button>
              ) : (
                <a href={p.href} className="btn-secondary h-8 text-xs px-3">
                  <Link2 size={12} />
                  {p.title}
                </a>
              )}
            </div>
            <p className="text-xs text-brand-muted mt-3">{linked ? p.title : p.desc}</p>
          </div>
        );
      })}
    </div>
  );
}